import { useCallback, useMemo } from "react"

import useReigniteMetadata from "../lib/firebase/useReigniteMetadata"

import { useGetMasterEventMetadataQuery } from "../api/reigniteApi"
import { skipToken } from "@reduxjs/toolkit/query"

type UseFirebaseApiSource = (masterId: number | null, eventId: number | null) => {
  apiSource: 'reignite' | 'alaska' | null
  isFetchingFirebase: boolean
}

const useValidIds = (masterId: number | null, eventId: number | null) => {
  const idsAlreadyExist = !!(masterId && eventId)

  const {
    data: masterReigniteData,
    isFetching: isMasterReigniteFetching,
  } = useGetMasterEventMetadataQuery(masterId ?? skipToken, { skip: idsAlreadyExist })

  const currentEventIdFromReigniteApi = useMemo(() => masterReigniteData?.currentEvent?.id || null, [masterReigniteData])

  if (idsAlreadyExist) {
    return { validMasterId: masterId, validEventId: eventId }
  }

  if (isMasterReigniteFetching) {
    return { isValidatingIds: true }
  }

  if (masterId && currentEventIdFromReigniteApi) {
    return { validMasterId: masterId, validEventId: currentEventIdFromReigniteApi }
  }

  return { validMasterId: null, validEventId: null }
}

const useFirebaseApiSource: UseFirebaseApiSource = (masterId, eventId) => {
  const { validMasterId, validEventId, isValidatingIds } = useValidIds(masterId, eventId)

  const { isFetchingFirebaseConfig, reigniteApiUsageConfig } = useReigniteMetadata()

  const checkIfReigniteCanBeUsed = useCallback((masterId: number, eventId: number, config: NonNullable<typeof reigniteApiUsageConfig>) => {
    if (!(masterId in config)) {
      return false
    }

    const master = config[masterId]

    const isMasterAllowed = master.blacklist.length === 0 && master.whitelist.length === 0
    const isEventAllowed = master.whitelist.includes(eventId) && !master.blacklist.includes(eventId)

    return isMasterAllowed || isEventAllowed
  }, [])

  const apiSource = useMemo(() => {
    if (!reigniteApiUsageConfig || !validMasterId || !validEventId) {
      return null
    }

    if (checkIfReigniteCanBeUsed(validMasterId, validEventId, reigniteApiUsageConfig)) {
      return 'reignite'
    }

    return 'alaska'
  }, [checkIfReigniteCanBeUsed, reigniteApiUsageConfig, validEventId, validMasterId])

  return {
    apiSource,
    isFetchingFirebase: isFetchingFirebaseConfig || !!isValidatingIds
  }
}

export { useFirebaseApiSource }